import type { CompetitionFormat } from '../models';

/**
 * Single source of truth for in-app URLs. Every internal <Link>/<Navigate>
 * should call into this module — the day we change a URL scheme, only
 * this file moves.
 *
 * ─── URL STANDARD ───────────────────────────────────────────────────────────
 * Competition workspace URLs use the human-readable slug (e.g. "winter-2026"),
 * NOT the internal ID (e.g. "tour-abc123"). The router resolves both, so old
 * bookmarks and legacy redirects still work, but every new link we generate
 * must use the slug and the correct surface:
 *   seasons     -> /admin/seasons/:slug
 *   tournaments -> /admin/tournaments/:slug
 *
 * Pattern for any code that builds a season/tournament URL:
 *
 *   import { paths, tournamentSlug } from '../lib/paths';
 *   <Link to={paths.adminCompetition(t.format, tournamentSlug(t))} />
 *
 * tournamentSlug(t) returns t.slug if set, otherwise falls back to t.id so
 * data created before the slug field existed still works.
 * ────────────────────────────────────────────────────────────────────────────
 */

/**
 * Returns the URL-safe identifier for a tournament — prefer the human-readable
 * slug, fall back to the internal ID for legacy records.
 */
export function tournamentSlug(t: { slug?: string | null; id: string }): string {
  return t.slug || t.id;
}

export function adminCompetitionBase(format: CompetitionFormat | undefined, slug: string): string {
  return (format ?? 'tournament') === 'season'
    ? `/admin/seasons/${slug}`
    : `/admin/tournaments/${slug}`;
}

export function adminCompetitionTab(format: CompetitionFormat | undefined, slug: string, tab: string): string {
  return `${adminCompetitionBase(format, slug)}/${tab}`;
}

export function adminCompetitionSettings(format: CompetitionFormat | undefined, slug: string): string {
  return `${adminCompetitionBase(format, slug)}/settings`;
}

export const paths = {
  // Public surface
  marketingHome: '/',
  events: '/events',
  event: (slug: string) => `/events/${slug}`,
  eventTab: (slug: string, tab: string) => `/events/${slug}/${tab}`,
  about: '/about',
  contact: '/contact',
  privacy: '/privacy',
  terms: '/terms',
  signIn: '/sign-in',
  register: (slug: string) => `/register/${slug}`,

  // Personal hub (any signed-in user)
  me: '/me',
  meInbox: '/me/inbox',

  // League HQ
  // NOTE: the `slug` parameter should be the result of tournamentSlug(t), not t.id directly.
  adminHome: '/admin',
  adminTournaments: '/admin/tournaments',
  adminTournamentNew: '/admin/tournaments/new',
  adminTournament: (slug: string) => `/admin/tournaments/${slug}`,
  adminTournamentTab: (slug: string, tab: string) => `/admin/tournaments/${slug}/${tab}`,
  adminTournamentTeams: (slug: string) => `/admin/tournaments/${slug}/teams`,
  adminTournamentSettings: (slug: string) => `/admin/tournaments/${slug}/settings`,
  adminSeasons: '/admin/seasons',
  adminSeasonNew: '/admin/seasons/new',
  adminSeason: (slug: string) => `/admin/seasons/${slug}`,
  adminSeasonTab: (slug: string, tab: string) => `/admin/seasons/${slug}/${tab}`,
  adminSeasonTeams: (slug: string) => `/admin/seasons/${slug}/teams`,
  adminSeasonSettings: (slug: string) => `/admin/seasons/${slug}/settings`,
  adminCompetition: (format: CompetitionFormat | undefined, slug: string) => adminCompetitionBase(format, slug),
  adminCompetitionTab: (format: CompetitionFormat | undefined, slug: string, tab: string) => adminCompetitionTab(format, slug, tab),
  adminCompetitionSettings: (format: CompetitionFormat | undefined, slug: string) => adminCompetitionSettings(format, slug),
  adminDirectory: '/admin/directory',
  adminComms: '/admin/comms',
  adminRegistration: '/admin/registration',
  adminInsights: '/admin/insights',
  adminBilling: '/admin/billing',
  adminReports: '/admin/reports',
  adminSettings: '/admin/settings',
  adminContact: '/admin/contact',
  adminScoreGame: (gameId: string) => `/admin/scoring/${gameId}`,
  adminDirectoryProfile: (profileId: string) => `/admin/directory/${profileId}`,

  // Scorekeeper company HQ (super-admin only)
  companyHome: '/company',
  companyPipeline: '/company/leads',
  companyLeads: '/company/leads',
  companyContacts: '/company/contacts',
  companyCustomers: '/company/customers',
  companyPricing: '/company/pricing',
  companyMarketing: '/company/marketing',
  companyFeedback: '/company/feedback',
};
